// src/theme.js
import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const arabicFontStack = [
  '"Droid Arabic Kufi"',
  '"DroidKufi-Regular"',
  '"DroidKufi-Bold"',
  '"IBM Plex Sans Arabic"',
  '"Alexandria"',
  '"Segoe UI"',
  'Tahoma',
  'sans-serif',
].join(', ');

const latinFontStack = [
  '"Segoe UI"',
  '"Roboto"',
  '"Helvetica"',
  '"Arial"',
  'sans-serif',
].join(', ');

export const buildAppTheme = (isRTL = true) => {
  const baseFontFamily = isRTL ? arabicFontStack : latinFontStack;

  let theme = createTheme({
    direction: isRTL ? 'rtl' : 'ltr',

    palette: {
      mode: 'light',
      primary: {
        main: '#4F46E5',
        light: '#818CF8',
        dark: '#3730A3',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#10B981',
        light: '#34D399',
        contrastText: '#ffffff',
      },
      background: {
        default: '#F1F5F9',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#0F172A',
        secondary: '#64748B',
      },
    },

    shape: {
      borderRadius: 16,
    },

    typography: {
      fontFamily: baseFontFamily,
      fontSize: 16,
      fontWeightRegular: 600,
      fontWeightMedium: 700,
      fontWeightBold: 800,
      h1: { fontFamily: baseFontFamily, fontWeight: 800, fontSize: '2.35rem', lineHeight: 1.25 },
      h2: { fontFamily: baseFontFamily, fontWeight: 800, fontSize: '2rem', lineHeight: 1.28 },
      h3: { fontFamily: baseFontFamily, fontWeight: 800, fontSize: '1.8rem', lineHeight: 1.3 },
      h4: { fontFamily: baseFontFamily, fontWeight: 800, fontSize: '1.6rem', lineHeight: 1.32, letterSpacing: '-0.3px' },
      h5: { fontFamily: baseFontFamily, fontWeight: 800, fontSize: '1.38rem', lineHeight: 1.34 },
      h6: { fontFamily: baseFontFamily, fontWeight: 800, fontSize: '1.18rem', lineHeight: 1.36 },
      subtitle1: { fontFamily: baseFontFamily, fontWeight: 700, fontSize: '1rem', lineHeight: 1.6 },
      subtitle2: { fontFamily: baseFontFamily, fontWeight: 700, fontSize: '0.94rem', lineHeight: 1.55 },
      body1: { fontFamily: baseFontFamily, fontWeight: 600, fontSize: '1rem', lineHeight: 1.7 },
      body2: { fontFamily: baseFontFamily, fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.65 },
      button: {
        fontFamily: baseFontFamily,
        fontWeight: 700,
        fontSize: '0.98rem',
        textTransform: 'none',
        lineHeight: 1.45,
      },
      caption: { fontFamily: baseFontFamily, fontWeight: 700, fontSize: '0.86rem', lineHeight: 1.45 },
      overline: { fontFamily: baseFontFamily, fontWeight: 700, fontSize: '0.84rem', lineHeight: 1.45, textTransform: 'none' },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html, body, #root': {
            fontFamily: `${baseFontFamily} !important`,
            backgroundColor: '#F1F5F9',
          },
          '*, *::before, *::after': {
            boxSizing: 'border-box',
          },
          body: {
            fontFamily: `${baseFontFamily} !important`,
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: 1.65,
          },
          'input, button, select, textarea, label, span, div, p, a, li': {
            fontFamily: `${baseFontFamily} !important`,
          },
        },
      },

      MuiTypography: {
        defaultProps: {
          variantMapping: {
            subtitle1: 'p',
            subtitle2: 'p',
          },
        },
        styleOverrides: {
          root: {
            fontFamily: baseFontFamily,
          },
        },
      },

      MuiButtonBase: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
          },
        },
      },

      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 700,
            fontSize: '0.96rem',
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 700,
            fontSize: '0.96rem',
            '&.Mui-focused': {
              fontFamily: `${baseFontFamily} !important`,
            },
          },
        },
      },

      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 600,
            fontSize: '0.88rem',
          },
        },
      },

      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
      },

      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 600,
            fontSize: '1rem',
          },
          input: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 600,
            fontSize: '1rem',
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            backgroundColor: 'rgba(248, 250, 252, 0.8)',
            fontFamily: `${baseFontFamily} !important`,
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover fieldset': {
              borderColor: '#CBD5E1',
            },
            '&.Mui-focused': {
              backgroundColor: '#FFFFFF',
              boxShadow: '0 0 0 4px rgba(79, 70, 229, 0.1)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#4F46E5',
              borderWidth: '2px',
            },
          },
          input: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 600,
            fontSize: '1rem',
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 600,
            fontSize: '0.98rem',
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            borderRadius: '12px',
            padding: '10px 24px',
            boxShadow: 'none',
            fontWeight: 700,
            fontSize: '0.98rem',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 20px -6px rgba(79, 70, 229, 0.4)',
            },
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 700,
            fontSize: '0.9rem',
          },
          label: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 700,
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontSize: '0.98rem',
            fontWeight: 600,
          },
          head: {
            fontWeight: 800,
            fontSize: '1rem',
          },
        },
      },

      MuiCardHeader: {
        styleOverrides: {
          title: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 800,
          },
          subheader: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 600,
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 800,
            fontSize: '1.15rem',
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 700,
            fontSize: '0.96rem',
            textTransform: 'none',
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '24px',
            boxShadow: '0 10px 40px -10px rgba(15,23,42,0.08)',
            border: '1px solid rgba(255,255,255,0.7)',
          },
        },
      },

      MuiDataGrid: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontSize: '1rem',
          },
          cell: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 600,
          },
          columnHeaderTitle: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 800,
            fontSize: '1rem',
          },
          toolbarContainer: {
            fontFamily: `${baseFontFamily} !important`,
            fontSize: '0.98rem',
          },
          footerContainer: {
            fontFamily: `${baseFontFamily} !important`,
            fontSize: '0.98rem',
          },
        },
      },
    },
  });

  theme = responsiveFontSizes(theme, {
    breakpoints: ['sm', 'md', 'lg'],
    factor: 2,
  });

  return theme;
};

export default buildAppTheme(true);
